import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {catchError, tap} from 'rxjs/operators';
import {IUser} from '../types/common.types';
import {Observable, of, Subject, throwError} from 'rxjs';
import {IDP_STATE_STORAGE_KEY} from '../modules/share/utils/constants';

@Injectable()
export class AuthService {
  public redirectUrl = '/contracts';
  private apiUrl = environment.api_url;
  unreadCounter: Subject<boolean> = new Subject<boolean>();
  updateUnreadCountObserve = this.unreadCounter.asObservable();
  unreadAgreementCount: Subject<boolean> = new Subject<boolean>();
  updateUnreadAgreementCountObserve = this.unreadAgreementCount.asObservable();
  constructor(
    public router: Router,
    private httpClient: HttpClient
  ) {}

  get isLoggedIn() {
    // Признак входа — сохранённый профиль. Прежде здесь была cookie SSO,
    // которую наш код не выставляет, а IDP в схеме OAuth2 не выдаёт:
    // из-за этого isLoggedIn всегда был false и вход был невозможен.
    return !!this.user;
  }
  get token() {
    return this.getCookie('SSO');
  }
  get lang() {
    return this.getCookie('lang') || this.getCookie('egovLang') || this.getCookie('locale') || 'ru';
  }
  saveSSO(sso: string) {
    this.setCookie('SC_SSO', sso);
    // window.localStorage.setItem('auth_token', sso);
  }
  get isDeviceMobile():boolean{
    return this.getCookie('X-Egov-Frame-Type') === 'MOBILE' || this.getCookie('egov-client-type') === 'MOBILE';
  }

  saveUser(user: any) {
    // if (user) {
    //   const parsedUser = this.parseRawDataToUser(user);
    //   localStorage.setItem('user', JSON.stringify(parsedUser));
    // }

    if (user) {
      if (user.sso) {
        this.saveSSO(user.sso);
      }
      const parsedUser = this.parseRawDataToUser(user);
      localStorage.setItem('user', JSON.stringify(parsedUser));
      // Set the cookie
      this.setCookie('user_data', JSON.stringify(parsedUser),{'max-age': 3600})
    }
  }
  get hasStoredUser(): boolean {
    return !!localStorage.getItem('user');
  }

  get user(): IUser {
    const cookieUserData = this.getCookie('user_data');
    const fromCookie = cookieUserData ? this.decodeUserData(cookieUserData) : undefined;
    if (fromCookie) {
      const normalized = JSON.stringify(fromCookie);
      if (cookieUserData !== normalized) {
        this.setCookie('user_data', normalized, {'max-age': 3600});
      }
      return fromCookie;
    }

    const rawUserItem = localStorage.getItem('user');
    const fromUserItem = rawUserItem ? this.decodeUserData(rawUserItem) : undefined;
    if (fromUserItem) {
      return fromUserItem;
    }

    const rawLocalUserData = localStorage.getItem('user_data');
    const fromLocalUserData = rawLocalUserData ? this.decodeUserData(rawLocalUserData) : undefined;
    return fromLocalUserData || null;
  }

  private decodeUserData(raw: string): any {
    const tryParse = (value: string): any => {
      try {
        return JSON.parse(value);
      } catch {
        return undefined;
      }
    };

    const direct = tryParse(raw);
    if (direct !== undefined) {
      return direct;
    }

    try {
      const bytes = Uint8Array.from(window.atob(raw), c => c.charCodeAt(0));
      const decoded = new TextDecoder('utf-8').decode(bytes);
      return tryParse(decoded);
    } catch {
      // не валидный base64 — не наш случай
      return undefined;
    }
  }

  get userBin(): string {
    const user: any = this.user;
    return user?.org?.bin || user?.bin || user?.legalProfile?.bin || '';
  }

  get signerIds(): string[] {
    return [this.user?.iin, this.userBin].filter((id): id is string => !!id);
  }

  get languageLocalStorage():any{
    return (
      this.getCookie('lang') || localStorage.getItem('language')
    );
  }
  get getUserInitial() {
    return this.user.first[0] + this.user.last[0];
  }
  get getUserName() {
    return (this.user) ? `${this.user.last} ${this.user.first && this.user.first[0]}.` : '';
  }
  updateUser(user: any) {
    localStorage.setItem('user', JSON.stringify(user));
  }
  updateUnreadCount(updated) {
    this.unreadCounter.next(updated);
  }
  updateUnreadAgreementCount(updated) {
    this.unreadAgreementCount.next(updated);
  }
  /**
   *     Gets profile by sso, when user authorized from idp.egov.kz
   */
  getProfile(): Observable<any> {
    return this.httpClient.get(`${this.apiUrl}user_profile`, {withCredentials: true})
      .pipe(tap((res: any) => {
        if (res) {
          this.saveUser(res);
        } else {
          this.logout();
        }
      },
      catchError((err: any) => {
        this.logout();
        return throwError(err);
      })));
  }

  // APP_INITIALIZER — до первого запроса приложения. Нет user в localStorage
  // — грузим профиль: страницы зовут getProfile() только при наличии
  // SC_SSO (auth.token), а её ставит сам этот ответ (saveUser → saveSSO),
  // поэтому без него профиль не загружался никогда. Есть user, но нет
  // SC_SSO — только восстанавливаем sso.
  restoreSSO(): Promise<void> {
    const needsUser = !this.hasStoredUser;
    if (!needsUser && this.token) {
      return Promise.resolve();
    }
    return fetch(`${this.apiUrl}user_profile`, {credentials: 'include'})
      .then(res => res.ok ? res.json() : null)
      .then(user => {
        if (!user) {
          return;
        }
        if (needsUser) {
          this.saveUser(user);
        } else if (user.sso) {
          this.saveSSO(user.sso);
        }
      })
      .catch(() => {});
  }

  getCookie(name: string) {
    const matches = document.cookie.match(new RegExp(
      '(?:^|; )' + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'
    ));
    return matches ? decodeURIComponent(matches[1]) : undefined;
  }
  setCookie(name, value, options: any = {}) {
    options = {
      path: '/',
      ...options
    };
    if (options.expires && options.expires.toUTCString) {
      options.expires = options.expires.toUTCString();
    }
    let updatedCookie = encodeURIComponent(name) + '=' + encodeURIComponent(value);
    for (const optionKey in options) {
      updatedCookie += '; ' + optionKey;
      const optionValue = options[optionKey];
      if (optionValue !== true) {
        updatedCookie += '=' + optionValue;
      }
    }
    document.cookie = updatedCookie;
  }
  deleteCookie(name) {
    this.setCookie(name, '', {'max-age': -1});
  }

  parseRawDataToUser(rawUser: any): IUser {
    return {
      iin: rawUser.iin,
      last: rawUser.lastname,
      first: rawUser.firstname,
      middle: rawUser.middlename || '',
      bin: rawUser.bin,
      org: {
        bin: rawUser.bin,
        ru: rawUser.orgNameRu,
        kk: rawUser.orgNameKk,
      },
      isLegal: !!rawUser.bin,
      fullName: `${rawUser.lastname} ${rawUser.firstname} ${rawUser.middlename || ''}`,
      // fullName: capitalizeFullName({last: rawUser.lastname, first: rawUser.firstname, middle: rawUser.middlename || ''}),
    };
  }

  /**
   * Обмен одноразового кода авторизации на сессию.
   * withCredentials обязателен: фронт на sc.egov.kz, бэк на api-sc.egov.kz —
   * без него браузер не сохранит cookie сессии из ответа.
   */
  sendCode(code: string): Observable<any> {
    return this.httpClient.get(`${this.apiUrl}get-token`, {params: {code}, withCredentials: true});
  }

  /** Код авторизации одноразовый: помним отправленные, чтобы F5 и «назад» не повторяли обмен. */
  private sentCodesKey = 'idp_sent_codes';

  isCodeAlreadySent(code: string): boolean {
    try {
      return (sessionStorage.getItem(this.sentCodesKey) || '').split(',').indexOf(code) !== -1;
    } catch (e) {
      return false;
    }
  }

  markCodeSent(code: string) {
    try {
      const sent = (sessionStorage.getItem(this.sentCodesKey) || '').split(',').filter(c => !!c);
      sent.push(code);
      sessionStorage.setItem(this.sentCodesKey, sent.slice(-5).join(','));
    } catch (e) {
      // приватный режим — защита деградирует до серверной (бэк код повторно в IDP не отправит)
    }
  }

  /** Сверяет state из callback с тем, что положили перед уходом на IDP. */
  checkIdpState(state: string): boolean {
    try {
      const expected = sessionStorage.getItem(IDP_STATE_STORAGE_KEY);
      sessionStorage.removeItem(IDP_STATE_STORAGE_KEY);
      if (!expected) {
        return true; // состояние потеряно (новый таб, приватный режим) — вход не блокируем
      }
      return expected === state;
    } catch (e) {
      return true;
    }
  }

  login(data: any): Observable<any> {
    return this.httpClient.post(`${this.apiUrl}user/login`, data, {headers: {'Content-Type': 'application/json'}})
      .pipe(
        tap(res => {
          if (res.message === 'SUCCESS') {
            this.saveUser(res.user);
          }
        })
      );
  }

  logout(redirect = true) {
    // Сессия живёт в HttpOnly-cookie бэка: из JS её не стереть, поэтому просим бэк погасить её.
    // keepalive — запрос доживает до ответа даже при немедленном уходе на IDP.
    try {
      fetch(`${this.apiUrl}logout`, {method: 'POST', credentials: 'include', keepalive: true}).catch(() => {});
    } catch (e) {}
    document.cookie = `SSO=; domain=${environment.domain}; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
    document.cookie = 'SSO=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/';
    this.deleteCookie('SC_SSO');
    document.cookie = 'user_data=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/';
    localStorage.removeItem('user');
    localStorage.removeItem('updatedContracts');
    this.deleteCookie('user_data');
    try {
      sessionStorage.removeItem(this.sentCodesKey);
    } catch (e) {}
    if (redirect) {
      this.router.navigate(['landing']);
    }
  }
  getUnReadsCount(): Observable<any> {
    return this.httpClient.get(`${this.apiUrl}v1/contracts/read/count-unread/${this.user.iin}`, { headers: {'Content-Type': 'application/json' }} );
  }
  getAgreementUnReadsCount(): Observable<any> {
    const params = {
      acceptor: this.user.iin,
    };
    return this.httpClient.get(`${this.apiUrl}agreement/unread_count/`, {params});
  }
}
